import { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "@/components/shared/Avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import CustomerActivityTimeline from "@/components/customers/CustomerActivityTimeline";
import {
  useMarkOpportunityLost,
  useUpdateOpportunityStage,
  type OpportunityWithRelations,
} from "@/hooks/use-opportunities";
import { useCustomerFollowUps } from "@/hooks/use-follow-ups";
import { formatDate } from "@/lib/format";
import { PRIORITY_LABELS } from "@/lib/follow-up-calc";
import { MOVABLE_STAGES, STAGE_LABELS, daysInStage, isOpenStage, opportunityPartyName } from "@/lib/opportunity-calc";
import { PIPE_STAGES } from "@/lib/pipe-board-calc";
import { cn } from "@/lib/utils";
import { NewOfferDialog } from "@/components/offer/NewOfferDialog";
import { toast } from "sonner";
import { useConvertLeadToCustomer } from "@/hooks/use-leads";
import BookMeetingFromRecord from "./BookMeetingFromRecord";
import OpportunityMeetings from "./OpportunityMeetings";
import MarkLostDialog from "./MarkLostDialog";
import NewFollowUpDialog from "./NewFollowUpDialog";

const SOURCE_LABELS = { manual: "Manuell", signal: "Signal", lead: "Lead" } as const;

interface OpportunityDrawerProps {
  opportunity: OpportunityWithRelations | null;
  onClose: () => void;
}

/** Detaljer for én prosess: stage-stepper, aktiviteter, oppfølginger og handlinger. */
export default function OpportunityDrawer({ opportunity, onClose }: OpportunityDrawerProps) {
  const [lostOpen, setLostOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [convertedCustomerId, setConvertedCustomerId] = useState<string | null>(null);
  const convertLead = useConvertLeadToCustomer();
  const updateStage = useUpdateOpportunityStage();
  const markLost = useMarkOpportunityLost();
  const { data: customerFollowUps = [] } = useCustomerFollowUps(opportunity?.customer_id ?? undefined);

  if (!opportunity) return null;
  const open = isOpenStage(opportunity.stage);
  const offerCustomerId = opportunity.customer_id ?? convertedCustomerId;
  // Lead-prosess uten kunde: kunden opprettes (uten PowerOffice) idet tilbudet lages.
  const startOffer = () => {
    if (offerCustomerId || !opportunity.lead_id) return setOfferOpen(true);
    convertLead.mutate(opportunity.lead_id, {
      onSuccess: (customerId) => {
        setConvertedCustomerId(customerId);
        toast.success("Kunde opprettet fra lead");
        setOfferOpen(true);
      },
    });
  };
  const followUps = customerFollowUps.filter((f) => f.opportunity_id === opportunity.id);
  const moveTo = opportunity.stage === "outreach" ? "meeting" : opportunity.stage === "meeting" ? "outreach" : null;

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{opportunityPartyName(opportunity)}</SheetTitle>
          <SheetDescription>{opportunity.title}</SheetDescription>
        </SheetHeader>

        <ol aria-label="Fase" className="mt-4 flex gap-1 text-sm">
          {PIPE_STAGES.map((s) => (
            <li
              key={s}
              aria-current={opportunity.stage === s ? "step" : undefined}
              className={cn(
                "flex-1 border-[1.5px] border-foreground/40 px-2 py-1 text-center",
                opportunity.stage === s && "border-foreground bg-foreground text-background",
              )}
            >
              {STAGE_LABELS[s]}
            </li>
          ))}
        </ol>
        {!open && (
          <p className="mt-2 text-sm text-muted-foreground">
            {STAGE_LABELS[opportunity.stage]}
            {opportunity.stage === "lost" && opportunity.lost_reason ? `: ${opportunity.lost_reason}` : ""}
          </p>
        )}

        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Eier</dt>
          <dd className="flex items-center gap-2">
            <Avatar name={opportunity.owner?.full_name} avatarUrl={opportunity.owner?.avatar_url} size="sm" />
            {opportunity.owner?.full_name ?? "Ukjent"}
          </dd>
          {opportunity.theme && (
            <>
              <dt className="text-muted-foreground">Tema</dt>
              <dd>{opportunity.theme}</dd>
            </>
          )}
          <dt className="text-muted-foreground">Kilde</dt>
          <dd>{SOURCE_LABELS[opportunity.source]}</dd>
          <dt className="text-muted-foreground">I fasen</dt>
          <dd>{daysInStage(opportunity.stage_entered_at, new Date())} dager</dd>
          {opportunity.offer_id && !open && (
            <>
              <dt className="text-muted-foreground">Tilbud</dt>
              <dd>
                <Link to={`/tilbud/${opportunity.offer_id}`} className="text-primary hover:underline">
                  Åpne tilbud
                </Link>
              </dd>
            </>
          )}
          {opportunity.project_id && (
            <>
              <dt className="text-muted-foreground">Prosjekt</dt>
              <dd>
                <Link to={`/prosjekter/${opportunity.project_id}`} className="text-primary hover:underline">
                  Åpne prosjekt
                </Link>
              </dd>
            </>
          )}
        </dl>

        {open && (
          <div className="mt-4 flex flex-wrap gap-2">
            {opportunity.offer_id ? (
              <Button asChild>
                <Link to={`/tilbud/${opportunity.offer_id}`}>Åpne tilbud</Link>
              </Button>
            ) : (
              (opportunity.customer_id || opportunity.lead_id) && (
                <Button onClick={startOffer} disabled={convertLead.isPending}>
                  Lag tilbud
                </Button>
              )
            )}
            {moveTo && (
              <Button
                variant="outline"
                disabled={updateStage.isPending}
                onClick={() => updateStage.mutate({ id: opportunity.id, stage: moveTo })}
              >
                Flytt til {STAGE_LABELS[moveTo]}
              </Button>
            )}
            {(opportunity.customer_id || opportunity.lead_id) && (
              <Button variant="outline" onClick={() => setBookOpen(true)}>
                Book møte
              </Button>
            )}
            <Button variant="outline" onClick={() => setFollowUpOpen(true)}>
              Ny oppfølging
            </Button>
            {opportunity.stage !== "offer" && (
              <Button variant="outline" onClick={() => setLostOpen(true)}>
                Marker tapt
              </Button>
            )}
          </div>
        )}
        <OpportunityMeetings opportunityId={opportunity.id} />
        {open && !MOVABLE_STAGES.includes(opportunity.stage) && (
          <p className="mt-2 text-xs text-muted-foreground">Prosessen i Tilbud følger tilbudet: marker tilbudet som vunnet eller tapt for å avslutte den.</p>
        )}

        <section aria-label="Oppfølginger på prosessen" className="mt-6 space-y-2">
          <h3 className="font-display text-base font-semibold">Oppfølginger</h3>
          {followUps.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ingen åpne oppfølginger på denne prosessen.</p>
          ) : (
            <ul className="divide-y divide-border/60 text-sm">
              {followUps.map((f) => (
                <li key={f.id} className="flex justify-between gap-3 py-1.5">
                  <span className="truncate">{f.title}</span>
                  <span className="shrink-0 text-muted-foreground">
                    {PRIORITY_LABELS[f.priority]} · {formatDate(f.due_date)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {opportunity.customer_id && (
          <div className="mt-6">
            <CustomerActivityTimeline customerId={opportunity.customer_id} opportunityId={opportunity.id} />
          </div>
        )}

        <MarkLostDialog
          open={lostOpen}
          onOpenChange={setLostOpen}
          title={opportunity.title}
          pending={markLost.isPending}
          onSubmit={(reason) =>
            markLost.mutate(
              { id: opportunity.id, reason },
              {
                onSuccess: () => {
                  setLostOpen(false);
                  onClose();
                },
              },
            )
          }
        />
        {offerCustomerId && !opportunity.offer_id && (
          <NewOfferDialog
            open={offerOpen}
            onOpenChange={setOfferOpen}
            initialCustomerId={offerCustomerId}
            initialTitle={opportunity.title}
            opportunityId={opportunity.id}
          />
        )}
        {(opportunity.customer_id || opportunity.lead_id) && (
          <BookMeetingFromRecord
            open={bookOpen}
            onOpenChange={setBookOpen}
            customerId={opportunity.customer_id}
            leadId={opportunity.lead_id}
            name={opportunityPartyName(opportunity)}
            opportunityId={opportunity.id}
            topic={opportunity.title}
          />
        )}
        {opportunity.customer_id && (
          <NewFollowUpDialog
            open={followUpOpen}
            onOpenChange={setFollowUpOpen}
            customerId={opportunity.customer_id}
            defaultOpportunityId={opportunity.id}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
